import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Badge,
  Button,
  Input,
  Modal,
  Select,
  Warning,
} from '@react-apps/design-system';
import { UFS } from '../../domain/entities/content.entity';
import type {
  Institution,
  InstitutionMergeCollision,
} from '../../domain/entities/content.entity';
import {
  useInstitutionsById,
  useMergeInstitutions,
} from '../hooks/use-institutions';
import { Mono, notifyDone, notifyFailed } from '../display';
import { translateInstitutionError } from './InstitutionFormModal';

/**
 * **Mesclar instituição**: a linha da esquerda some dentro da da direita, e as
 * provas dela trocam de dono.
 *
 * ## Por que esta tela existe
 *
 * Porque arquivar uma instituição com prova é recusado (`INSTITUTION_IN_USE`) e
 * não há como trocar o `institutionId` de uma prova — então a duplicata
 * (`UNICAMP` e `Unicamp` vivas ao mesmo tempo, que a sigla não-normalizada
 * permite) ficava para sempre no seletor e partia a incidência da mesma
 * instituição real em dois ids. O tooltip do botão de arquivar já mandava
 * "mescle na linha certa" antes de existir por onde.
 *
 * ## Três decisões de UX, e o porquê de cada uma
 *
 * 1. **Confirmação por digitação, não por clique.** A mescla reescreve até 500
 *    provas, arquiva uma instituição e **não tem desfazer** — restaurar devolve
 *    a linha, não as provas. Um `[Confirmar]` custa o mesmo que um clique
 *    errado. Digitar a sigla que **sai** obriga a ler qual das duas está
 *    desaparecendo, que é justamente o erro caro aqui (mesclar na direção
 *    inversa).
 * 2. **A colisão é tabela, não frase.** O 409 traz uma lista de coordenadas
 *    `(ano, variante)` que existem nas duas instituições, e cada linha é uma
 *    prova que alguém precisa abrir e resolver. Achatar isso num toast jogaria
 *    fora a única informação acionável da resposta.
 * 3. **O resumo fala em números, não em conceitos.** "3 provas passam de
 *    (Unicamp-SP) para (UNICAMP-SP)" é verificável antes de clicar; "as
 *    referências serão atualizadas" não é.
 */
export function MergeInstitutionModal({
  source,
  onClose,
}: {
  /** A instituição que **sai**. `null` fecha o modal. */
  source: Institution | null;
  onClose: () => void;
}) {
  const { data: institutionsById } = useInstitutionsById();
  const merge = useMergeInstitutions();

  const [uf, setUf] = useState('');
  const [targetId, setTargetId] = useState('');
  const [confirmation, setConfirmation] = useState('');

  // Cada abertura começa limpa: um alvo herdado da mescla anterior é o tipo de
  // estado que faz alguém confirmar a operação errada.
  useEffect(() => {
    if (!source) return;
    setUf(source.uf);
    setTargetId('');
    setConfirmation('');
    merge.reset();
    // `merge` é estável o suficiente aqui; depender dele reabriria o efeito a
    // cada mutação e limparia o formulário no meio do envio.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source?.id]);

  const target = targetId ? institutionsById?.get(targetId) : undefined;

  /**
   * Candidatos a alvo: **vivos, da UF escolhida, e não a própria origem**.
   *
   * Arquivada fica fora porque o back recusa (`findLiveById`): mover prova para
   * uma linha que não se oferece mais é o estado que a mescla existe para
   * desfazer. A própria origem fica fora porque mesclar algo em si mesmo é
   * `INVALID_ARGUMENT` — melhor não oferecer do que traduzir o 400.
   */
  const targetOptions = useMemo(() => {
    if (!source) return [];
    return [...(institutionsById?.values() ?? [])]
      .filter((i) => i.uf === uf && i.deletedAt === null && i.id !== source.id)
      .sort((a, b) => a.initials.localeCompare(b.initials))
      .map((i) => ({
        value: i.id,
        label: `${label(i)} — ${i.examCount} ${i.examCount === 1 ? 'prova' : 'provas'}`,
      }));
  }, [institutionsById, uf, source]);

  const collisions = readMergeCollisions(merge.error);
  const canSubmit =
    !!source &&
    !!target &&
    confirmation.trim().toUpperCase() === source.initials.toUpperCase() &&
    !merge.isPending;

  const submit = () => {
    if (!source || !target) return;
    merge.mutate(
      { sourceInstitutionId: source.id, targetInstitutionId: target.id },
      {
        onSuccess: (result) => {
          onClose();
          const movidas = `${result.movedExamCount} ${result.movedExamCount === 1 ? 'prova' : 'provas'}`;
          if (result.projectionsRebuilt) {
            notifyDone(
              `Mescla concluída — ${movidas} agora em ${label(result.target)}`,
            );
            return;
          }
          // Sucesso com ressalva, NÃO erro: a prata está certa e repetir a
          // mescla não é o caminho (a origem já está arquivada e sem provas).
          notifyFailed(
            null,
            `Mescla concluída (${movidas}), mas a incidência não foi reconstruída — rode o rebuild das projeções quando puder`,
          );
        },
        // Erro fica NO modal: a lista de colisões é para trabalhar, e um toast
        // some antes de a pessoa ler a primeira linha.
      },
    );
  };

  if (!source) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title="Mesclar instituição"
      description="A instituição da esquerda some dentro da da direita, e as provas dela trocam de instituição."
    >
      <div className="space-y-5">
        <div className="flex items-stretch gap-3">
          <SideCard
            role="sai"
            institution={source}
            examCount={source.examCount}
          />
          <div className="text-(--color-text-secondary) flex items-center">
            <ArrowRight size="md" />
          </div>
          <SideCard
            role="fica"
            institution={target}
            examCount={target?.examCount}
          />
        </div>

        <div className="flex items-end gap-2">
          <div className="w-24 shrink-0">
            <Select
              label="UF"
              value={uf}
              // Trocar de UF limpa o alvo: manter selecionado algo que saiu da
              // lista é como se confirma uma mescla que não se leu.
              onChange={(e) => {
                setUf(e.target.value);
                setTargetId('');
              }}
              options={UFS.map((u) => ({ value: u, label: u }))}
              placeholder="UF"
            />
          </div>
          <Select
            label="Instituição que fica (primária)"
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            options={targetOptions}
            placeholder={
              targetOptions.length === 0
                ? 'Nenhuma outra instituição ativa nesta UF'
                : 'Selecione…'
            }
            disabled={targetOptions.length === 0}
          />
        </div>

        {target && collisions.length === 0 && (
          <WhatWillHappen source={source} target={target} />
        )}

        {collisions.length > 0 && (
          <CollisionTable
            collisions={collisions}
            source={source}
            target={target}
          />
        )}

        {merge.error && collisions.length === 0 && (
          <p className="text-sm text-red-600">
            {translateInstitutionError(merge.error, 'Falha ao mesclar')}
          </p>
        )}

        {target && collisions.length === 0 && (
          <Input
            label={`Digite ${source.initials} para confirmar`}
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder={source.initials}
            helperText="A sigla da instituição que SAI — é o que confirma a direção da mescla."
            autoComplete="off"
          />
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={submit}
            disabled={!canSubmit}
          >
            {merge.isPending
              ? 'Mesclando…'
              : source.examCount > 0
                ? `Mesclar ${source.examCount} ${source.examCount === 1 ? 'prova' : 'provas'}`
                : 'Mesclar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Um dos dois lados do "X → Y". Sem alvo escolhido, é um placeholder. */
function SideCard({
  role,
  institution,
  examCount,
}: {
  role: 'sai' | 'fica';
  institution?: Institution;
  examCount?: number;
}) {
  const isSource = role === 'sai';
  return (
    <div
      className={`border-(--color-border) bg-(--color-bg-secondary) flex-1 rounded-lg border p-3 ${
        isSource ? 'opacity-90' : ''
      }`}
    >
      <div className="mb-1.5">
        <Badge variant={isSource ? 'warning' : 'success'}>
          {isSource ? 'SAI — será arquivada' : 'FICA — primária'}
        </Badge>
      </div>
      {institution ? (
        <>
          <p className="text-(--color-text-primary) font-semibold">
            <Mono>
              ({institution.initials}-{institution.uf})
            </Mono>
          </p>
          <p className="text-(--color-text-secondary) text-sm">
            {institution.name}
          </p>
          <p className="text-(--color-text-secondary) mt-1 text-xs">
            {examCount ?? 0} {examCount === 1 ? 'prova' : 'provas'}
          </p>
        </>
      ) : (
        <p className="text-(--color-text-secondary) py-2 text-sm">
          Selecione abaixo
        </p>
      )}
    </div>
  );
}

/**
 * O resumo em números. Quatro linhas, e nenhuma delas é genérica — cada uma
 * corresponde a um efeito real e verificável depois.
 */
function WhatWillHappen({
  source,
  target,
}: {
  source: Institution;
  target: Institution;
}) {
  const provas = `${source.examCount} ${source.examCount === 1 ? 'prova' : 'provas'}`;
  return (
    <div className="border-(--color-border) bg-(--color-bg-tertiary) space-y-1.5 rounded-lg border p-3 text-sm">
      <p className="text-(--color-text-primary) flex items-center gap-1.5 font-medium">
        <Warning size="sm" /> O que vai acontecer
      </p>
      <ul className="text-(--color-text-secondary) list-inside list-disc space-y-1">
        {source.examCount > 0 && (
          <li>
            {provas} {source.examCount === 1 ? 'passa' : 'passam'} de{' '}
            <Mono>{label(source)}</Mono> para <Mono>{label(target)}</Mono>.
          </li>
        )}
        <li>
          <Mono>
            ({source.initials}-{source.uf})
          </Mono>{' '}
          é arquivada: sai do seletor de prova e do CSV modelo.
        </li>
        <li>
          Planilhas com a sigla <Mono>{source.initials}</Mono> passam a falhar
          na validação — o rótulo aceito vira{' '}
          <Mono>
            ({target.initials}-{target.uf})
          </Mono>
          .
        </li>
        <li>
          <strong>Não há desfazer:</strong> restaurar devolve a linha, não as
          provas.
        </li>
      </ul>
    </div>
  );
}

/**
 * A recusa por coordenada duplicada, como lista de trabalho.
 *
 * O ponto da última frase: depois de uma operação em lote recusada, a primeira
 * pergunta de quem operou é "então o que ficou gravado?". A resposta é nada — e
 * dizer isso vale mais que repetir o motivo do erro.
 */
function CollisionTable({
  collisions,
  source,
  target,
}: {
  collisions: InstitutionMergeCollision[];
  source: Institution;
  target?: Institution;
}) {
  return (
    <div className="space-y-2 rounded-lg border border-red-300 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/30">
      <p className="text-sm font-medium text-red-700 dark:text-red-400">
        Não foi possível mesclar: {collisions.length}{' '}
        {collisions.length === 1 ? 'prova existe' : 'provas existem'} nas duas
        instituições com a mesma coordenada.
      </p>
      <p className="text-(--color-text-secondary) text-xs">
        Resolva cada uma antes de mesclar: apague a duplicata, ou mude a
        variante de uma das duas.
      </p>
      <table className="w-full text-xs">
        <thead className="text-(--color-text-secondary)">
          <tr className="text-left">
            <th className="py-1 pr-2">Ano</th>
            <th className="py-1 pr-2">Variante</th>
            <th className="py-1 pr-2">Em {source.initials}</th>
            <th className="py-1">Em {target?.initials ?? 'destino'}</th>
          </tr>
        </thead>
        <tbody className="text-(--color-text-primary)">
          {collisions.map((c) => (
            <tr key={`${c.year}-${c.variant}`}>
              <td className="py-1 pr-2">{c.year}</td>
              <td className="py-1 pr-2">{c.variant}</td>
              <td className="py-1 pr-2">
                <ExamLink contentId={c.sourceExamContentId} />
              </td>
              <td className="py-1">
                <ExamLink contentId={c.targetExamContentId} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-(--color-text-secondary) text-xs font-medium">
        Nenhuma prova foi alterada.
      </p>
    </div>
  );
}

/** Abre o preview da prova em outra aba — sem perder o modal. */
function ExamLink({ contentId }: { contentId: string }) {
  return (
    <Link
      to={`/content/exams/${contentId}`}
      target="_blank"
      rel="noreferrer"
      className="text-(--color-accent) underline"
    >
      abrir
    </Link>
  );
}

/** `(SIGLA-UF)` — a parte do rótulo que identifica, sem o nome por extenso. */
function label(institution: Institution): string {
  return `(${institution.initials}-${institution.uf})`;
}

/**
 * As colisões do `details` do 409 `INSTITUTION_MERGE_CONFLICT`.
 *
 * Lista vazia para qualquer outro erro — é o que o modal usa para decidir entre
 * a tabela e a frase.
 */
export function readMergeCollisions(err: unknown): InstitutionMergeCollision[] {
  if (!err || typeof err !== 'object') return [];
  const body = (err as { response?: { data?: unknown } }).response?.data;
  if (!body || typeof body !== 'object') return [];
  const typed = body as { error?: unknown; details?: unknown };
  if (typed.error !== 'INSTITUTION_MERGE_CONFLICT') return [];
  const details = typed.details;
  if (!details || typeof details !== 'object') return [];
  const collisions = (details as { collisions?: unknown }).collisions;
  return Array.isArray(collisions)
    ? (collisions as InstitutionMergeCollision[])
    : [];
}
